import {
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Stack,
  Text
} from '@mantine/core'
import {
  IconCircleCheckFilled,
  IconPlayerPlay,
  IconPlayerStop,
  IconPlus,
  IconRefresh,
  IconTrash
} from '@tabler/icons-react'
import { useEffect, useRef, useState } from 'react'
import { FormattedMessage } from 'react-intl'
import { useInvalidateDeviceList } from '@/queries/deviceQueries'
import { Device, TowerAction, TowerServiceStatus } from '@/types/device'
import { getTowerStatus, isJetsonDevice } from '@/utils/device/getDeviceAttr'
import { isUpdateAvailable, normalizeVersion } from '@/utils/version'
import { DeviceTypeBadge } from '../../devices/DeviceTypeBadge/DeviceTypeBadge'
import { OnlineStatus } from '../../devices/OnlineStatus/OnlineStatus'
import { TowerActionConfirmModal } from '../TowerActionConfirmModal/TowerActionConfirmModal'
import { TowerStatusBadge } from '../TowerStatusBadge/TowerStatusBadge'
import {
  OPTIMISTIC_STATUS_TIMEOUT_MS,
  optimisticStatusForAction,
  shouldClearOptimisticStatus
} from '../towerOptimisticStatus'
import {
  DEFAULT_TOWER_STATUS,
  TOWER_DEPLOY_REJECTED_STATE,
  TOWER_UNINSTALLED_STATE,
  isKnownTowerStatus,
  isTowerBusyStatus,
  isTowerErrorCode
} from '../towerStatus'

// ThingsBoard attributes update after the RPC; wait before refetching the list.
const DEVICE_STATUS_REFETCH_DELAY_MS = 1000

type TowerDeviceCardProps = {
  device: Device
  canManage?: boolean
  latestVersion?: string
}

type OptimisticStatus = {
  status: TowerServiceStatus
  reportedWhenSet: TowerServiceStatus
}

export const TowerDeviceCard = ({
  device,
  canManage = true,
  latestVersion
}: TowerDeviceCardProps) => {
  const [pendingAction, setPendingAction] = useState<TowerAction | null>(null)
  const [optimistic, setOptimistic] = useState<OptimisticStatus | null>(null)
  const { invalidateDeviceList } = useInvalidateDeviceList()
  const refetchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (refetchTimerRef.current !== null) {
        clearTimeout(refetchTimerRef.current)
      }
    }
  }, [])

  const isJetson = isJetsonDevice(device)
  const towerStatus = getTowerStatus(device)
  const reportedStatus = towerStatus?.service_status ?? DEFAULT_TOWER_STATUS
  const towerServiceStatus = optimistic?.status ?? reportedStatus
  const isRejected = towerStatus?.state === TOWER_DEPLOY_REJECTED_STATE
  const isUninstalled = towerStatus?.state === TOWER_UNINSTALLED_STATE
  const isTowerInProgress =
    isTowerBusyStatus(towerServiceStatus) ||
    !isKnownTowerStatus(towerServiceStatus)

  const showInstall =
    towerServiceStatus === 'initializing' ||
    isUninstalled ||
    towerServiceStatus === 'failed'
  const showStart = towerServiceStatus === 'stopped' && !isUninstalled
  const showStop =
    towerServiceStatus === 'running' || towerServiceStatus === 'degraded'
  const showRestart =
    towerServiceStatus === 'running' || towerServiceStatus === 'degraded'
  const showUninstall =
    !isUninstalled &&
    (towerServiceStatus === 'running' ||
      towerServiceStatus === 'degraded' ||
      towerServiceStatus === 'stopped' ||
      towerServiceStatus === 'failed')
  const showActions =
    canManage && !isJetson && device.active && !isTowerInProgress
  const showStatus = !isJetson && device.active

  const currentVersion = normalizeVersion(towerStatus?.version)
  const publishedVersion = normalizeVersion(latestVersion)
  const hasUpdate = isUpdateAvailable(currentVersion, publishedVersion)
  const showVersion =
    showStatus &&
    Boolean(currentVersion) &&
    !isUninstalled &&
    towerServiceStatus !== 'initializing'
  const showUpdate =
    showActions &&
    hasUpdate &&
    !showInstall &&
    (towerServiceStatus === 'running' ||
      towerServiceStatus === 'degraded' ||
      towerServiceStatus === 'stopped')

  const rejectionError = towerStatus?.error
  const progressDetail = towerStatus?.detail

  useEffect(() => {
    if (
      optimistic &&
      shouldClearOptimisticStatus(reportedStatus, optimistic.reportedWhenSet)
    ) {
      setOptimistic(null)
    }
  }, [optimistic, reportedStatus])

  useEffect(() => {
    if (!optimistic) {
      return
    }

    const timeoutId = window.setTimeout(() => {
      setOptimistic(null)
    }, OPTIMISTIC_STATUS_TIMEOUT_MS)

    return () => window.clearTimeout(timeoutId)
  }, [optimistic])

  const handleActionClick = (action: TowerAction) => {
    setPendingAction(action)
  }

  const handleModalClose = () => {
    setPendingAction(null)
  }

  const handleActionSuccess = (action: TowerAction) => {
    const nextStatus = optimisticStatusForAction(action)

    if (nextStatus) {
      setOptimistic({
        status: nextStatus,
        reportedWhenSet: reportedStatus
      })
    }

    if (refetchTimerRef.current !== null) {
      clearTimeout(refetchTimerRef.current)
    }
    refetchTimerRef.current = setTimeout(() => {
      refetchTimerRef.current = null
      void invalidateDeviceList()
    }, DEVICE_STATUS_REFETCH_DELAY_MS)
  }

  const renderStatusDetail = () => {
    if (isRejected && rejectionError) {
      return (
        <Text size="xs" c="red">
          {isTowerErrorCode(rejectionError) ? (
            <FormattedMessage id={`tower.hardware.error.${rejectionError}`} />
          ) : (
            rejectionError
          )}
        </Text>
      )
    }

    if (isTowerInProgress) {
      return (
        <Text size="xs" c="dimmed">
          {progressDetail ?? (
            <FormattedMessage id="tower.hardware.busy.detail" />
          )}
        </Text>
      )
    }

    if (towerServiceStatus === 'failed') {
      const failedMessage = towerStatus?.error ?? progressDetail

      if (failedMessage) {
        return (
          <Text size="xs" c="red">
            {isTowerErrorCode(failedMessage) ? (
              <FormattedMessage id={`tower.hardware.error.${failedMessage}`} />
            ) : (
              failedMessage
            )}
          </Text>
        )
      }
    }

    if (progressDetail) {
      return (
        <Text size="xs" c="dimmed">
          {progressDetail}
        </Text>
      )
    }

    return null
  }

  return (
    <Card withBorder>
      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Stack gap={4}>
          <Group gap="xs">
            <Text fw={600}>{device.label}</Text>
            <DeviceTypeBadge isJetson={isJetson} />
          </Group>

          <OnlineStatus status={device.active ? 'online' : 'offline'} />
        </Stack>

        <Paper radius="md" p="md" bg="gray.1">
          <Text fw={600} size="sm" mb="lg">
            <FormattedMessage id="tower.title" />
          </Text>

          <Stack gap="xl">
            {showStatus && (
              <Stack gap={4}>
                <Group gap="xs">
                  <TowerStatusBadge status={towerServiceStatus} />
                  {isTowerInProgress && <Loader size={14} />}
                </Group>

                {showVersion && (
                  <Group wrap="nowrap" gap="xs" align="center">
                    <Badge size="sm" color="gray">
                      {currentVersion}
                    </Badge>

                    {!hasUpdate && publishedVersion && (
                      <IconCircleCheckFilled
                        size={18}
                        color="var(--mantine-color-green-6)"
                      />
                    )}

                    {showUpdate && (
                      <Button
                        size="compact-xs"
                        variant="outline"
                        onClick={() => handleActionClick('update')}
                      >
                        <FormattedMessage
                          id="tower.hardware.installUpdate"
                          values={{ version: publishedVersion }}
                        />
                      </Button>
                    )}
                  </Group>
                )}

                {renderStatusDetail()}
              </Stack>
            )}

            {showActions && (
              <Group gap="xs">
                {showInstall && (
                  <Button
                    size="xs"
                    miw={100}
                    leftSection={<IconPlus size={16} />}
                    onClick={() => handleActionClick('deploy')}
                  >
                    <FormattedMessage id="tower.hardware.deploy" />
                  </Button>
                )}

                {showStart && (
                  <Button
                    size="xs"
                    color="green"
                    miw={100}
                    leftSection={<IconPlayerPlay size={16} />}
                    onClick={() => handleActionClick('start')}
                  >
                    <FormattedMessage id="tower.hardware.start" />
                  </Button>
                )}

                {showStop && (
                  <Button
                    size="xs"
                    variant="light"
                    color="red"
                    miw={100}
                    leftSection={<IconPlayerStop size={16} />}
                    onClick={() => handleActionClick('stop')}
                  >
                    <FormattedMessage id="tower.hardware.stop" />
                  </Button>
                )}

                {showRestart && (
                  <Button
                    size="xs"
                    variant="light"
                    color="orange"
                    miw={100}
                    leftSection={<IconRefresh size={16} />}
                    onClick={() => handleActionClick('restart')}
                  >
                    <FormattedMessage id="tower.hardware.restart" />
                  </Button>
                )}

                {showUninstall && (
                  <Button
                    size="xs"
                    variant="subtle"
                    color="red"
                    leftSection={<IconTrash size={16} />}
                    onClick={() => handleActionClick('uninstall')}
                  >
                    <FormattedMessage id="tower.hardware.uninstall" />
                  </Button>
                )}
              </Group>
            )}

            {isJetson && (
              <Text size="xs" c="yellow">
                <FormattedMessage id="tower.hardware.jetsonWarning" />
              </Text>
            )}

            {!device.active && !isJetson && (
              <Text size="xs" c="yellow">
                <FormattedMessage id="tower.hardware.deviceOfflineWarning" />
              </Text>
            )}
          </Stack>
        </Paper>
      </SimpleGrid>

      {pendingAction && (
        <TowerActionConfirmModal
          action={pendingAction}
          deviceId={device.id}
          latestVersion={publishedVersion}
          opened
          onClose={handleModalClose}
          onSuccess={handleActionSuccess}
        />
      )}
    </Card>
  )
}
